import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMatchAcceptModal } from '../../contexts/MatchAcceptModalContext';
import { API_URL } from '../../utils/api';
import { buildWebSocketUrl } from '../../utils/websocket';
import { subscribeQueueEvents } from '../../utils/queueEventBus';
import { clearMatchDraft, getMatchDraft, subscribeMatchDraft } from '../../utils/matchDraft';
import MapPoolSelector from '../veto/MapPoolSelector';
import VetoFlow from '../veto/VetoFlow';
import StepPlayerSelection from './StepPlayerSelection';
import StepTeams from './StepTeams';
import StepGameMode from './StepGameMode';
import StepConnect from './StepConnect';
import StreamControl from '../matches/StreamControl';
import MatchPrediction from '../matches/MatchPrediction';
import FeatureBoundary from '../common/FeatureBoundary';
import useAdminPermissions from '../../hooks/useAdminPermissions';
import './CreateMatch.css';

const STEPS_CREATE = ['players', 'teams', 'game_mode', 'map_pool', 'veto', 'launching', 'connect'];
const STEPS_LOBBY = ['teams', 'game_mode', 'map_pool', 'veto', 'launching', 'connect'];
const STEP_LABEL_KEYS = {
  players: 'nav.players',
  teams: 'createMatch.teams',
  game_mode: 'nav.play',
  map_pool: 'nav.maps',
  veto: 'createMatch.veto',
  launching: 'createMatch.launching',
  connect: 'nav.match',
};

/**
 * Match creation wizard — handles both create mode and queue lobby mode.
 */
export default function CreateMatch() {
  const { t } = useTranslation();
  const { matchid: urlMatchId } = useParams();
  const isQueueMatch = !!urlMatchId;

  const { user, isLoggedIn, getAuthHeaders, login } = useAuth();
  const { openMatchAcceptModal } = useMatchAcceptModal();
  const navigate = useNavigate();
  // Streaming-kontroll (owner) på Redo-vyn så streamen kan startas redan när
  // servern är uppe (STARTED), innan matchen är LIVE. Embed:en visas bara på Live.
  const { isOwner } = useAdminPermissions();
  const [step, setStep] = useState(isQueueMatch ? 0 : 0);
  const [config, setConfig] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [matchType, setMatchType] = useState('BO1');
  const [match, setMatch] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlineIds, setOnlineIds] = useState(new Set());
  // {discord_id: match_id} for players currently in CREATED/STARTED/LIVE
  // matches. Used by StepPlayerSelection to grey out busy players and by
  // handleCreateMatch as a final guard before POSTing.
  const [busyMap, setBusyMap] = useState({});
  const [gameMode, setGameMode] = useState(null);
  const [weaponMode, setWeaponMode] = useState('ak');
  const [vetoState, setVetoState] = useState(null);
  const [connectInfo, setConnectInfo] = useState(null);
  const [steamLink, setSteamLink] = useState(null);
  const intervalRef = useRef(null);
  const wsRef = useRef(null);

  const steps = isQueueMatch ? STEPS_LOBBY : STEPS_CREATE;

  // Leader detection for queue matches
  const authorDiscordId = match?.author?.user_id;
  const isLeader = !isQueueMatch || (
    user?.discord_id && authorDiscordId &&
    String(authorDiscordId) === String(user.discord_id)
  );

  // Find leader name for non-leader messaging
  const leaderName = (() => {
    if (!isQueueMatch || !match) return 'Leader';
    const allPlayers = [
      ...(match.team1?.players || []),
      ...(match.team2?.players || []),
    ];
    const leader = allPlayers.find(p =>
      p.discord_user && String(p.discord_user.user_id) === String(authorDiscordId)
    );
    return leader?.steam_user?.username || leader?.discord_user?.username || 'Leader';
  })();

  // Fetch config (always needed for guild_id), players only in create mode
  useEffect(() => {
    fetch(`${API_URL}/api/web/config`).then(r => r.json()).then(cfg => {
      setConfig(cfg);
      if (!isQueueMatch && cfg?.guild?.guild_id) {
        fetch(`${API_URL}/api/web/players/${cfg.guild.guild_id}`)
          .then(r => r.json())
          .then(data => setPlayers(data));
        fetch(`${API_URL}/api/queue/${cfg.guild.guild_id}/online`)
          .then(r => r.json())
          .then(data => setOnlineIds(new Set(data?.online || [])))
          .catch(() => {});
      }
    });
  }, [isQueueMatch]);

  // Live-merge av match-utkastet från Online-sidebarens ➕-knappar.
  //
  // Utkastet är en TRANSIENT kanal, inte en engångskonsumtion: varje spelare
  // som läggs till i sidebaren och finns i `players` flyttas in i
  // selectedPlayers och tas sedan ur utkastet. Det gör att ett andra ➕-klick
  // (efter att wizarden redan öppnats) LÄGGER TILL i den påbörjade matchen i
  // stället för att tappas — vi prenumererar på utkastet och kör mergen vid
  // varje ändring, inte bara vid mount.
  const mergeDraft = useCallback(() => {
    if (isQueueMatch || players.length === 0) return;
    const draft = getMatchDraft();
    if (draft.length === 0) return;
    const draftIds = new Set(draft.map(p => String(p.discord_id)));
    const matched = players.filter(p => draftIds.has(String(p.discord_id)));
    if (matched.length > 0) {
      setSelectedPlayers(prev => {
        const have = new Set(prev.map(p => String(p.discord_id)));
        const additions = matched.filter(p => !have.has(String(p.discord_id)));
        return additions.length ? [...prev, ...additions] : prev;
      });
    }
    clearMatchDraft();
  }, [isQueueMatch, players]);

  // Kör vid mount och när spelarlistan laddats (utkast som väntade), och
  // prenumerera så sidebarens ➕ slår igenom direkt på den öppna wizarden.
  useEffect(() => { mergeDraft(); }, [mergeDraft]);
  useEffect(() => subscribeMatchDraft(() => mergeDraft()), [mergeDraft]);

  // Poll which players are already in an active match so we can disable
  // them in the picker. Only needed in create mode — lobby mode is past
  // selection. 20s cadence is enough for typical session pace; the final
  // guard in handleCreateMatch catches races inside that window.
  useEffect(() => {
    if (isQueueMatch) return;
    let cancelled = false;
    const fetchBusy = () => {
      fetch(`${API_URL}/api/web/players-in-active-matches`)
        .then(r => (r.ok ? r.json() : {}))
        .then(map => { if (!cancelled) setBusyMap(map || {}); })
        .catch(() => {});
    };
    fetchBusy();
    const id = setInterval(fetchBusy, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isQueueMatch]);

  // Determine initial step for lobby matches based on match state (runs once on mount)
  const initialStepDetected = useRef(false);
  useEffect(() => {
    if (!isQueueMatch || !urlMatchId || initialStepDetected.current) return;
    initialStepDetected.current = true;
    const detectStep = async () => {
      try {
        const [matchRes, vetoRes] = await Promise.all([
          fetch(`${API_URL}/api/web/matches/${urlMatchId}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/veto/${urlMatchId}/state`, { headers: getAuthHeaders() }).catch(() => null),
        ]);
        if (!matchRes.ok) return;
        const matchData = await matchRes.json();
        setMatch(matchData);

        // Determine if current user is the match leader (can't use isLeader — match state not set yet)
        const matchAuthorId = matchData?.author?.user_id;
        const userIsLeader = user?.discord_id && matchAuthorId &&
          String(matchAuthorId) === String(user.discord_id);

        // Infer game mode and weapon mode from player count
        const playerCount = (matchData.team1?.players?.length || 0) + (matchData.team2?.players?.length || 0);
        if (playerCount <= 2) {
          setGameMode('duel');
          if (matchData.weapon_mode) setWeaponMode(matchData.weapon_mode);
        } else if (playerCount > 4) {
          setGameMode('competitive');
        }

        // Already has connect info → connect step
        if (matchData.connect_command) {
          setConnectInfo(matchData.connect_command);
          setSteamLink(matchData.steam_connect_link || null);
          setStep(steps.indexOf('connect'));
          return;
        }

        // Server started/live but no connect yet → launching
        if (matchData.status === 'STARTED' || matchData.status === 'LIVE') {
          setStep(steps.indexOf('launching'));
          return;
        }

        // Check veto state
        const vetoData = vetoRes?.ok ? await vetoRes.json() : null;
        if (vetoData) {
          setVetoState(vetoData);
          if (vetoData.phase === 'voting') {
            setStep(steps.indexOf('veto'));
          } else if (vetoData.phase === 'complete') {
            // Veto done — leader sees results, others go to launching
            setStep(userIsLeader ? steps.indexOf('veto') : steps.indexOf('launching'));
          }
          return;
        }

        // No veto yet — stay at step 0 (teams) so normal flow proceeds
      } catch (e) {
        console.error('Step detection error:', e);
      }
    };
    detectStep();
  }, [isQueueMatch, urlMatchId]);

  // Tournament match detection — redirect to tournament page
  useEffect(() => {
    if (!isQueueMatch || !urlMatchId) return;
    fetch(`${API_URL}/api/tournaments/by-match/${urlMatchId}/`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tournament_id) {
          navigate(`/tournaments/${data.tournament_id}`, { replace: true });
        }
      })
      .catch(() => {});
  }, [isQueueMatch, urlMatchId, getAuthHeaders, navigate]);

  // Fetch match data + poll during lobby/launching
  useEffect(() => {
    const matchId = isQueueMatch ? urlMatchId : match?.id;
    if (!matchId) return;
    const currentPhase = steps[step];

    // Only poll during relevant phases
    const pollPhases = isQueueMatch
      ? ['teams', 'map_pool', 'veto', 'launching']
      : ['launching'];
    if (!pollPhases.includes(currentPhase)) return;

    const fetchMatch = async () => {
      try {
        const response = await fetch(`${API_URL}/api/web/matches/${matchId}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          if (response.status === 404) {
            setError(t('createMatch.matchCancelledOrMissing'));
            clearInterval(intervalRef.current);
            return;
          }
          console.warn(`Transient match poll error: HTTP ${response.status}`);
          return;
        }
        const data = await response.json();
        setMatch(data);
        setError(prev => (prev === 'Failed to fetch match' ? null : prev));

        // Sync gameMode/weaponMode from match data (for non-leaders joining)
        if (data.weapon_mode && !gameMode) {
          const pc = (data.team1?.players?.length || 0) + (data.team2?.players?.length || 0);
          if (pc <= 2) setGameMode('duel');
          setWeaponMode(data.weapon_mode);
        }

        // If match already has a winner, redirect to match page
        if (data.winner_team) {
          clearInterval(intervalRef.current);
          navigate(`/stats/matches/${matchId}`, { replace: true });
          return;
        }

        // If veto is done (maplist finalized), advance past map_pool/veto
        // Leader sees VetoResults page first and advances manually
        const vetoPhases = ['map_pool', 'veto'];
        if (!isLeader && vetoPhases.includes(steps[step]) && data.maplist && data.num_maps &&
            data.maplist.length === data.num_maps) {
          setStep(steps.indexOf('launching'));
        }

        // If connect info already available, jump to connect phase
        if (data.connect_command) {
          setConnectInfo(data.connect_command);
          setSteamLink(data.steam_connect_link || null);
          setError(null);
          if (steps[step] !== 'connect') {
            setStep(steps.indexOf('connect'));
          }
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        console.warn('Transient match poll network error:', err.message);
      }
    };

    fetchMatch();
    const interval = currentPhase === 'launching' ? 3000 : 5000;
    intervalRef.current = setInterval(fetchMatch, interval);
    return () => clearInterval(intervalRef.current);
  }, [isQueueMatch, urlMatchId, match?.id, getAuthHeaders, step]);

  // WebSocket for queue match events (veto_complete, match_cancelled)
  useEffect(() => {
    if (!isQueueMatch) return;

    const wsUrl = buildWebSocketUrl(`/api/veto/ws/${urlMatchId}`);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'veto_complete' && !isLeader) {
          setStep(steps.indexOf('launching'));
        }
      } catch (e) {}
    };

    ws.onclose = () => {};

    return () => ws.close();
  }, [isQueueMatch, urlMatchId]);

  // Reset /create wizard state if the match we're building gets cancelled
  // by someone else (or auto-cancelled by a decline). The accept modal
  // itself is owned globally by App.js; this just keeps the local wizard
  // from being stuck on a stale match. Toast + lobby-route navigate are
  // handled by useQueueWebSocket — we only handle the /create-route case
  // where no navigate happens.
  useEffect(() => {
    if (isQueueMatch) return;
    const localMatchId = match?.id;
    if (!localMatchId) return;
    return subscribeQueueEvents((data) => {
      const isMine = String(data.match_id) === String(localMatchId);
      if (!isMine) return;
      const isCancel =
        data.event === 'match_cancelled' ||
        (data.event === 'match_accept_update' && data.cancelled);
      if (!isCancel) return;
      // Föredra cancelled_by_pk (voice-agnostiskt) över cancelled_by (legacy
      // discord_id). Båda check:as så vi täcker både gamla och nya publishers
      // — steam-only users saknar discord_id.
      const isCancellorByPk = data.cancelled_by_pk && user?.player_id
        && String(data.cancelled_by_pk) === String(user.player_id);
      const isCancellorByDiscord = data.cancelled_by && user?.discord_id
        && String(data.cancelled_by) === String(user.discord_id);
      if (isCancellorByPk || isCancellorByDiscord) return;
      setMatch(null);
      setStep(0);
    });
  }, [isQueueMatch, match?.id, user?.discord_id, user?.player_id]);

  // Auto-set game mode based on player count when entering game_mode step
  // Returns the auto mode if the step can be skipped (no user choice needed)
  const inferDefaultGameMode = (m) => {
    const count = (m?.team1?.players?.length || 0) + (m?.team2?.players?.length || 0);
    // Duel: always show step for weapon selection
    if (count <= 2) return null;
    if (count > 4) return 'competitive';
    return null; // 3-4 players: user must choose
  };

  const togglePlayer = (player) => {
    setSelectedPlayers(prev => {
      const exists = prev.find(p => p.discord_id === player.discord_id);
      if (exists) return prev.filter(p => p.discord_id !== player.discord_id);
      if (prev.length >= 10) return prev;
      return [...prev, player];
    });
  };

  const handleCreateMatch = async () => {
    if (!isLoggedIn) { login(); return; }

    // Last-second guard against creating a duplicate match while one is
    // already pending for the creator OR any selected player. The poller
    // updates busyMap every 20s; this catches a click that lands inside
    // that window before the backend itself rejects (defense in depth).
    const myId = String(user?.discord_id || '');
    const busyIds = new Set([
      ...(myId && busyMap[myId] ? [myId] : []),
      ...selectedPlayers
        .map(p => String(p.discord_id))
        .filter(id => busyMap[id]),
    ]);
    if (busyIds.size > 0) {
      setError(
        t('createMatch.cannotCreateBusy', { count: busyIds.size }),
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/web/matches`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discord_users_ids: selectedPlayers.map(p => p.discord_id),
          author_id: user.discord_id,
          guild_id: config.guild.id,
          match_type: matchType,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail?.message || err.detail || 'Failed to create match');
      }

      const matchData = await response.json();
      setMatch(matchData);

      // Trigger the global accept modal locally — don't wait for the
      // match_created WebSocket roundtrip (which historically drops the
      // first event sporadically). Pass the selectedPlayers list so the
      // modal can render names immediately; the backend publish has only
      // discord_id strings.
      if (matchData.pending_accept) {
        openMatchAcceptModal({
          matchId: matchData.id,
          statuses: matchData.accept_statuses || {},
          players: selectedPlayers.map(p => ({
            discord_id: String(p.discord_id),
            username: p.steam_username || p.discord_username || String(p.discord_id),
          })),
        });
      }

      // Always navigate off /create. Same component handles /lobby/:id —
      // it loads the existing match and shows team/veto/lobby UI. This
      // prevents the creator from double-submitting via the same button.
      navigate(`/lobby/${matchData.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShuffle = async (type = 'balanced') => {
    if (!match) return;
    setLoading(true);
    try {
      const endpoint = type === 'balanced' ? 'shuffle' : 'random-shuffle';
      const response = await fetch(`${API_URL}/api/web/matches/${match.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ interaction_user_id: user.discord_id }),
      });
      const data = await response.json();
      setMatch(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Persist a custom team name override for team1 or team2.
   *
   * @param {1|2} teamNumber - Which team's name to change.
   * @param {string} name - New display name (1-32 chars after trim).
   *   Maps to Match.team1_name / team2_name on the backend; Match.get_config()
   *   uses it when building MatchZy's team payload so the override flows
   *   through to matchzy_stats_matches.team1_name/team2_name.
   */
  const handleTeamNameChange = async (teamNumber, name) => {
    if (!match) return;
    const trimmed = (name || '').trim().slice(0, 32);
    if (!trimmed) return;
    setLoading(true);
    try {
      const field = teamNumber === 1 ? 'team1_name' : 'team2_name';
      const response = await fetch(`${API_URL}/api/web/matches/${match.id}/update`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: trimmed }),
      });
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      const data = await response.json();
      setMatch(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMovePlayer = async (playerId, toTeam) => {
    if (!match) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/web/matches/${match.id}/move-player`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: playerId,
          to_team: toTeam,
          interaction_user_id: user.discord_id,
        }),
      });
      const data = await response.json();
      setMatch(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVetoStarted = (state) => {
    if (state.phase === 'complete') {
      // No veto needed (pool size = maps needed), skip to next
      handleVetoComplete();
      return;
    }
    setVetoState(state);
    setStep(steps.indexOf('veto'));
  };

  const handleVetoComplete = async () => {
    setStep(steps.indexOf('launching'));
    // Launch match if not already launched (queue matches are launched by bot,
    // but we try anyway — if already started, the poll will pick up connect info)
    try {
      const res = await fetch(`${API_URL}/api/web/matches/${match.id}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.status === 'CREATED') {
        await fetch(`${API_URL}/api/web/matches/${match.id}/launch`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(gameMode === 'duel' && weaponMode ? { weapon_mode: weaponMode } : {}),
        });
      }
    } catch (_) {
      // Ignore — poll will handle connect transition
    }
  };

  const handleCancel = async () => {
    if (!match) return;
    if (!window.confirm('Vill du verkligen avbryta matchen?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/web/matches/${match.id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cancelled_by: user?.discord_id,
          cancelled_by_pk: user?.player_id,
        }),
      });
      if (!response.ok) throw new Error('Cancel failed');
      if (isQueueMatch) {
        navigate('/');
      } else {
        setMatch(null);
        setVetoState(null);
        setStep(0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="create-match-page">
        <h1 className="create-match-title">Create Match</h1>
        <div className="login-prompt">
          <p>You need to be logged in to create a match.</p>
          <button className="login-prompt-btn" onClick={login}>Login with Discord</button>
        </div>
      </div>
    );
  }

  // Loading state for queue matches before match data arrives
  if (isQueueMatch && !match && !error) {
    return (
      <div className="create-match-page">
        <div className="loading-screen"><div className="loader" /></div>
      </div>
    );
  }

  if (error && isQueueMatch) {
    return (
      <div className="create-match-page">
        <div className="create-match-error">{error}</div>
      </div>
    );
  }

  const currentStep = steps[step];

  return (
    <div className="create-match-page">
      <div className="create-match-header">
        <h1 className="create-match-title">
          {isQueueMatch ? `Match #${urlMatchId}` : 'Create Match'}
          {match?.type && <span className="lobby-match-type">{match.type}</span>}
        </h1>
        {match && currentStep !== 'launching' && currentStep !== 'connect' && isLeader && (
          <button className="cancel-match-btn" onClick={handleCancel} disabled={loading}>
            Avbryt match
          </button>
        )}
      </div>

      <div className="steps-indicator">
        {steps.filter(s => {
          // Hide game_mode step when it's auto-selected (2 or 6+ players)
          if (s === 'game_mode' && match) {
            return !inferDefaultGameMode(match);
          }
          return true;
        }).map((s, i) => {
          const realIdx = steps.indexOf(s);
          return (
            <div key={s} className={`step ${realIdx === step ? 'active' : ''} ${realIdx < step ? 'done' : ''}`}>
              <span className="step-num">{i + 1}</span>
              <span className="step-label">{STEP_LABEL_KEYS[s] ? t(STEP_LABEL_KEYS[s]) : s}</span>
            </div>
          );
        })}
      </div>

      {error && <div className="create-match-error">{error}</div>}

      {currentStep === 'players' && (
        <StepPlayerSelection
          players={players}
          selectedPlayers={selectedPlayers}
          onlineIds={onlineIds}
          busyMap={busyMap}
          matchType={matchType}
          onTogglePlayer={togglePlayer}
          onMatchTypeChange={setMatchType}
          onCreateMatch={handleCreateMatch}
          loading={loading}
        />
      )}

      {currentStep === 'teams' && match && (
        <StepTeams
          match={match}
          isQueueMatch={isQueueMatch}
          isLeader={isLeader}
          leaderName={leaderName}
          loading={loading}
          onShuffle={handleShuffle}
          onMovePlayer={handleMovePlayer}
          onTeamNameChange={handleTeamNameChange}
          onBack={() => setStep(0)}
          onNext={() => {
            const auto = inferDefaultGameMode(match);
            if (auto) {
              setGameMode(auto);
              setStep(steps.indexOf('map_pool'));
            } else {
              const count = (match?.team1?.players?.length || 0) + (match?.team2?.players?.length || 0);
              if (count <= 2 && !gameMode) setGameMode('duel');
              else if (!gameMode) setGameMode('competitive');
              setStep(steps.indexOf('game_mode'));
            }
          }}
        />
      )}

      {currentStep === 'game_mode' && match && (
        <StepGameMode
          match={match}
          gameMode={gameMode}
          weaponMode={weaponMode}
          onSelect={setGameMode}
          onWeaponSelect={setWeaponMode}
          onBack={() => setStep(steps.indexOf('teams'))}
          onNext={() => {
            // Persist weapon_mode on match for duel so it survives navigation
            if (gameMode === 'duel' && weaponMode && match?.id) {
              fetch(`${API_URL}/api/web/matches/${match.id}/update`, {
                method: 'PATCH',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ weapon_mode: weaponMode }),
              }).catch(() => {});
            }
            setStep(steps.indexOf('map_pool'));
          }}
        />
      )}

      {currentStep === 'map_pool' && match && (
        <div className="step-content">
          {isQueueMatch && !isLeader ? (
            <div className="lobby-waiting-text">
              {t('createMatch.leaderPicksMaps', { name: leaderName })}
            </div>
          ) : (
            <>
              <MapPoolSelector match={match} gameMode={gameMode} onVetoStarted={handleVetoStarted} />
              <div className="step-nav">
                <button className="back-btn" onClick={() => {
                  const auto = inferDefaultGameMode(match);
                  setStep(steps.indexOf(auto ? 'teams' : 'game_mode'));
                }}>
                  {t('profile.back')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {currentStep === 'veto' && match && (
        <div className="step-content">
          <VetoFlow matchId={match.id} initialState={vetoState} onComplete={handleVetoComplete} gameMode={gameMode} weaponMode={weaponMode} />
        </div>
      )}

      {currentStep === 'launching' && (
        <div className="step-content lobby-launching">
          <div className="loader" />
          <h2>{t('createMatch.startingServer')}</h2>
          {match?.maplist?.length > 0 && (
            <p className="launching-map">{match.maplist.map(mapName => {
              const name = mapName.startsWith('de_') ? mapName.replace('de_', '').charAt(0).toUpperCase() + mapName.replace('de_', '').slice(1) : mapName;
              return name;
            }).join(', ')}</p>
          )}
          {gameMode === 'duel' && weaponMode && (
            <p className="launching-weapon">{t('createMatch.weapon')}: {{ak:'AK-47',m4a4:'M4A4',m4a1s:'M4A1-S',deagle:'Desert Eagle',awp:'AWP'}[weaponMode] || weaponMode}</p>
          )}
          <p>{t('createMatch.serverStarting')}</p>
        </div>
      )}

      {currentStep === 'connect' && connectInfo && (
        <StepConnect match={match} connectInfo={connectInfo} steamLink={steamLink} />
      )}

      {/* Stream-kontroll (owner) på Redo-vyn — starta streamen tidigt. Embed:en
          visas ENDAST på Live-sidan (inte här). */}
      {currentStep === 'connect' && isOwner && urlMatchId && (
        <StreamControl matchId={urlMatchId} isOwner={isOwner} />
      )}

      {/* Matchtips: lobbyn är enda vyn som är nåbar under hela tipsfönstret
          (lagen satta → GOING_LIVE). Stats-matchsidan finns i praktiken inte
          förrän matchen är igång, och då är tipset redan låst. */}
      {/* FeatureBoundary: tipsrutan (med marknaden i sig) renderas mitt i
          matchstarten. Utan egen boundary hade en krasch i poängekonomin
          ersatt hela lobbyn med route-boundaryns felkort — alltså inget veto
          och ingen launch-knapp. Tipset får försvinna; matchen får inte. */}
      {['veto', 'launching', 'connect'].includes(currentStep) && match && (
        <FeatureBoundary name="MatchPrediction">
          <MatchPrediction key={currentStep} matchId={match.id} compact />
        </FeatureBoundary>
      )}

    </div>
  );
}
